/**
 * Number and report formatting shared by the hypothesis-test calculators.
 *
 * Two audiences read these numbers. The widget shows exact values in the reader's own
 * number format (`useToolNumber`), and the APA line follows the *Publication Manual*
 * (7th ed., 2020) §6.36 and Table 6.5: two decimals for most statistics, three for p,
 * no leading zero for a statistic that cannot exceed 1 (p, r, η², V), and "p < .001"
 * rather than an exact value below a thousandth. Statistic symbols in Latin letters are
 * italic (*t*, *F*, *p*, *N*, *d*); Greek letters are not (χ², η², ω², φ).
 *
 * Everything takes a formatter rather than a locale so it stays pure and testable, and
 * so a translated page writes its decimal mark the way its readers do.
 */

import { t } from "@/lib/i18n/interpolate";
import type { CitationParts } from "@/lib/tool-kit/citation-parts";

export type NumberFormatter = (
  value: number,
  options?: Intl.NumberFormatOptions
) => string;

/** English formatting — the default for tests and for callers without a locale. */
export const englishFormatter: NumberFormatter = (value, options) =>
  value.toLocaleString("en-US", options);

/** The real minus sign, which is what typeset statistics use. */
export const MINUS = "−";

/**
 * A fixed-decimal number with a true minus sign and no digit grouping.
 *
 * `leadingZero: false` drops the zero before the decimal mark, for statistics that
 * cannot exceed 1 in absolute value (APA §6.36). A value that rounds to zero is written
 * without a sign, so a tiny negative difference never prints as "−0.00". The zero is
 * found by formatting 0 rather than by matching "0", so a locale that writes its own
 * digits loses its own leading zero.
 */
export function fixed(
  value: number,
  decimals: number,
  fmt: NumberFormatter = englishFormatter,
  leadingZero = true
): string {
  if (!Number.isFinite(value)) {
    return value > 0 ? "∞" : `${MINUS}∞`;
  }
  const magnitude = Math.abs(value);
  const roundsToZero = magnitude < 0.5 * 10 ** -decimals;
  let body = fmt(magnitude, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    useGrouping: false,
  });
  const zero = fmt(0);
  if (
    !leadingZero &&
    magnitude < 1 &&
    body.startsWith(zero) &&
    body.length > zero.length
  ) {
    body = body.slice(zero.length);
  }
  const sign = value < 0 && !roundsToZero ? MINUS : "";
  return sign + body;
}

/**
 * Degrees of freedom as APA prints them: whole numbers bare, a fractional df (Welch,
 * Satterthwaite) to two decimals.
 */
export function formatDf(
  df: number,
  fmt: NumberFormatter = englishFormatter
): string {
  if (Math.abs(df - Math.round(df)) < 1e-9) {
    return fmt(Math.round(df), { useGrouping: false });
  }
  return fixed(df, 2, fmt);
}

/**
 * The p in an APA line, relation included: "= .033", "< .001", or "> .999" for a value
 * that would otherwise round to 1.000.
 */
export function apaP(
  p: number,
  fmt: NumberFormatter = englishFormatter
): string {
  if (p < 0.001) {
    return `< ${fixed(0.001, 3, fmt, false)}`;
  }
  if (p >= 0.9995) {
    return `> ${fixed(0.999, 3, fmt, false)}`;
  }
  return `= ${fixed(p, 3, fmt, false)}`;
}

const SUPERSCRIPT_DIGITS = "⁰¹²³⁴⁵⁶⁷⁸⁹";

function superscript(exponent: number): string {
  const digits = String(Math.abs(exponent))
    .split("")
    .map((digit) => SUPERSCRIPT_DIGITS[Number(digit)])
    .join("");
  return exponent < 0 ? `⁻${digits}` : digits;
}

/**
 * An exact p-value for display: four decimals down to .001, three significant digits
 * below that, and scientific notation (3.21 × 10⁻⁸) once four decimals would read as
 * zero.
 */
export function formatP(
  p: number,
  fmt: NumberFormatter = englishFormatter
): string {
  if (!Number.isFinite(p)) {
    return "—";
  }
  if (p >= 0.001) {
    return fmt(p, { minimumFractionDigits: 4, maximumFractionDigits: 4 });
  }
  if (p >= 0.0001) {
    return fmt(p, { maximumSignificantDigits: 3 });
  }
  if (p === 0) {
    return fmt(0);
  }
  const exponent = Math.floor(Math.log10(p));
  let mantissa = p / 10 ** exponent;
  let power = exponent;
  if (Number(mantissa.toFixed(2)) >= 10) {
    mantissa /= 10;
    power += 1;
  }
  return `${fmt(mantissa, { maximumFractionDigits: 2 })} × 10${superscript(power)}`;
}

/** A value for a results card or a working line: up to four decimals. */
export function formatStat(
  value: number,
  fmt: NumberFormatter = englishFormatter,
  digits = 4
): string {
  if (!Number.isFinite(value)) {
    return value > 0 ? "∞" : `${MINUS}∞`;
  }
  if (Math.abs(value) < 0.5 * 10 ** -digits) {
    return fmt(0);
  }
  const text = fmt(value, { maximumFractionDigits: digits });
  return text.startsWith("-") ? `${MINUS}${text.slice(1)}` : text;
}

/**
 * An APA template, with placeholders filled and `*asterisks*` turned into italics.
 *
 * The template comes from a widget's strings — `"*t*({df}) = {t}, *p* {p}"` — so the
 * wording of the line is translatable while the numbers come from the calculation. Only
 * the template's own asterisks toggle italics: it is split before the values go in, so
 * a value can never open or close an italic run.
 */
export function richTemplate(
  template: string,
  values: Record<string, string>
): CitationParts {
  const parts: CitationParts = [];
  for (const [index, segment] of template.split("*").entries()) {
    const text = t(segment, values);
    if (text === "") {
      continue;
    }
    parts.push(index % 2 === 1 ? { text, italic: true } : { text });
  }
  return parts;
}

/** Two-sided, lower-tail and upper-tail alternatives, as every test here names them. */
export type Alternative = "two-sided" | "less" | "greater";

export const ALTERNATIVES: readonly Alternative[] = [
  "two-sided",
  "less",
  "greater",
];
